import React from 'react';
import { Box, Theme, Typography } from '@mui/material';
import { styled } from '@mui/system';
import { IconInfoCircle } from '@tabler/icons-react';

import { Tooltip } from '@zendesk/forethought-elements';
import { ColorMode } from 'src/components/app/types';
import { NA } from 'src/constants/solve';

interface InnerDataOverviewCardProps {
  appCuesTarget?: string;
  bottomRow?: React.ReactNode;
  cardLabel: React.ReactNode;
  cardValue: React.ReactNode;
  colorMode?: ColorMode;
  isChild?: boolean;
  isParent?: boolean;
  labelColor?: string;
  size?: 'md' | 'lg';
  smallTitle?: boolean;
  tooltipContent?: string;
}

export default function InnerDataOverviewCard({
  appCuesTarget,
  bottomRow,
  cardLabel,
  cardValue,
  colorMode = 'light',
  isChild = false,
  isParent = false,
  labelColor,
  size = 'md',
  tooltipContent,
}: InnerDataOverviewCardProps) {
  const getTitleTypography = () => {
    if (isChild) {
      return 'font12Medium';
    }

    return 'font14Bold';
  };

  const getLabelColor = (theme: Theme) => {
    if (colorMode === 'dark') {
      return isChild ? 'rgba(255, 255, 255, 0.7)' : 'rgba(255, 255, 255, 0.9)';
    }
    return isChild
      ? theme.palette.colors.grey[700]
      : theme.palette.colors.grey[800];
  };

  const getValueColor = (theme: Theme) => {
    if (colorMode === 'dark') {
      return cardValue === NA
        ? 'rgba(255, 255, 255, 0.5)'
        : labelColor || '#fff';
    }
    return cardValue === NA ? theme.palette.colors.grey[600] : labelColor;
  };

  const getIconColor = () => {
    return colorMode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'currentColor';
  };

  const label = (
    <LabelContainer>
      <Box
        sx={theme => ({
          ...theme.typography[getTitleTypography()],
          color: getLabelColor(theme),
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          textTransform: isChild ? undefined : 'capitalize',
          textWrap: isChild ? 'wrap' : undefined,
          ...(colorMode === 'dark' && {
            fontFamily: 'IBM plex mono !important',
            fontSize: '12px',
            fontWeight: 500,
          }),
        })}
        title={typeof cardLabel === 'string' ? cardLabel : undefined}
      >
        {cardLabel}
      </Box>
      {tooltipContent && (
        <Tooltip tooltipContent={tooltipContent}>
          <IconInfoCircle color={getIconColor()} height={16} width={16} />
        </Tooltip>
      )}
    </LabelContainer>
  );
  const fontSize = size === 'lg' ? '32' : colorMode === 'dark' ? '28' : '24';

  return (
    <Box
      data-appcues-target={appCuesTarget}
      sx={{
        alignItems: 'flex-start',
        display: 'flex',
        flex: isChild ? 1 : undefined,
        flexDirection: 'column',
        gap: '4px',
        padding: isChild ? 0 : '4px 16px',
        ...(colorMode === 'dark' &&
          !isChild && {
            backdropFilter: 'blur(30px)',
            background:
              'linear-gradient(135deg, rgba(255, 255, 255, 0.015) 0%, rgba(255, 255, 255, 0.05) 100%)',
            border: '0.5px solid rgba(255, 255, 255, 0.08)',
            borderRadius: '24px',
            boxShadow:
              'inset 0 1px 1px 0 rgba(255, 255, 255, 0.1), 0 2px 16px 0 rgba(0, 0, 0, 0.05)',
            justifyContent: 'space-between',
            minHeight: '129px',
            minWidth: '235px',
            padding: '16px 18px',
            position: 'relative',
            width: '100%',
          }),
      }}
    >
      {!isChild && label}
      {isParent ? (
        <Box display='flex' gap={3}>
          {cardValue}
        </Box>
      ) : (
        <>
          {/* TODO: replace this with typography */}
          <Box
            sx={
              bottomRow
                ? {}
                : {
                    '.MuiTypography-root': {
                      fontFamily:
                        colorMode === 'dark'
                          ? 'IBM Plex Sans !important'
                          : undefined,
                      fontSize: `${fontSize}px`,
                      lineHeight: '32px',
                      whiteSpace: 'nowrap',
                    },
                    minWidth: 0,
                    width: '100%',
                  }
            }
          >
            <Typography
              sx={{ color: theme => getValueColor(theme) }}
              variant={`font${fontSize}`}
            >
              <LineHeightSpan>{cardValue}</LineHeightSpan>
            </Typography>
            {isChild && label}
          </Box>
        </>
      )}

      {bottomRow}
    </Box>
  );
}

const LabelContainer = styled('div')`
  display: flex;
  align-items: center;
  gap: 4px;
  white-space: nowrap;
  max-width: 100%;
`;

const LineHeightSpan = styled('span')`
  line-height: 44px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: block;
  max-width: 100%;
`;
